import { firebaseEmulatorsEnabled } from '../config/env';
import { isDemoAuthEnabled } from './demoAuth';
import { getDemoUser } from './demoUsers';
import { isDemoCredential, resolveDemoRole, DEMO_PASSWORD } from './demoCredentials';
import {
  usesFirebaseAuth,
  signInWithCredentials,
  reauthenticateWithPassword,
} from '../services/FirebaseAuthService';
import type { AppUser } from '../types';

export async function signInAppUser(username: string, password: string): Promise<AppUser | null> {
  if (!usesFirebaseAuth()) {
    if (!isDemoAuthEnabled()) return null;
    const role = resolveDemoRole(username);
    if (role && password === DEMO_PASSWORD) return getDemoUser(role);
    return null;
  }

  if (isDemoCredential(username, password)) {
    try {
      return await signInWithCredentials(username, password);
    } catch {
      if (isDemoAuthEnabled() && firebaseEmulatorsEnabled()) {
        const role = resolveDemoRole(username);
        if (role) return getDemoUser(role);
      }
      throw new Error('demo-auth-unavailable');
    }
  }

  return signInWithCredentials(username, password);
}

/**
 * Confirms the signed-in user's identity (password) without creating a new session.
 * Prefer device biometrics when available; this is the password fallback.
 */
export async function confirmAppUserIdentity(
  username: string,
  password: string,
): Promise<boolean> {
  if (!password.trim()) return false;

  if (!usesFirebaseAuth()) {
    if (!isDemoAuthEnabled()) return false;
    return isDemoCredential(username, password);
  }

  try {
    await reauthenticateWithPassword(password);
    return true;
  } catch {
    if (isDemoCredential(username, password) && isDemoAuthEnabled() && firebaseEmulatorsEnabled()) {
      return true;
    }
    return false;
  }
}
